import { useMemo } from 'react';
import { View, StyleSheet } from 'react-native';
import { Button, Text, theme, styles as baseStyles, Icon } from '@socrait/react';
import { QueryStatus } from '@tanstack/react-query';

import { AccordionBase, useAccordionState, type CopyText } from '~/components/Accordion';
import { ErrorBox } from '~/components/ErrorBox';
import { InsightsResponse } from '~/services/review/useInsightsQuery';
interface InsightsProps {
  data?: InsightsResponse | null;
  status?: QueryStatus | null;
}

export const Insights = ({ data, status }: InsightsProps) => {
  const { isCollapsed, accordionState, handleToggleCollapse } = useAccordionState();

  const handleButtonClick = () => {
    handleToggleCollapse(!isCollapsed);
  };

  const contentText = useMemo(() => {
    return data?.insights[0]?.students.map((student) => student.voiceName).join(', ') || '';
  }, [data]);

  const copyText = useMemo((): CopyText => {
    const safeText = contentText || '';
    return {
      plain: safeText,
      html: safeText,
    };
  }, [contentText]);

  const viewButton = (
    <Button
      name="View"
      onPress={handleButtonClick}
      containerStyle={[styles.viewButton, !isCollapsed && styles.viewButtonExpanded]}
      fontStyle={styles.viewButtonText}
      icon={!isCollapsed ? 'topArrow' : undefined}
    />
  );

  const content = useMemo(() => {
    return (
      <View style={styles.contentContainer}>
        <Text style={styles.contentText}>
          This week's data is available for {data?.sessionCount} class session{data?.sessionCount === 1 ? '' : 's'}.
          Here are the students who had 0-1 behavior warnings this week:
        </Text>
        <View style={styles.studentsContainer}>
          <Text style={styles.contentText}>{contentText}</Text>
        </View>
      </View>
    );
  }, [data, contentText]);

  return (
    <>
      {!data?.insights?.length || data?.session?.status === 'IN_PROGRESS' ? null : (
        <View style={styles.container}>
          <Text style={styles.title}>Insights</Text>
          {status === 'error' ? (
            <ErrorBox text="Could not load weekly insights. Please try refreshing the page." />
          ) : (
            <AccordionBase
              title={`**${data?.insights[0]?.students.length} students had great behavior this week!**`}
              icon={<Icon type="smallStar" fill={theme.colors.navy} />}
              rightControls={viewButton}
              content={content}
              isCollapsed={isCollapsed}
              onToggleCollapse={handleToggleCollapse}
              state={accordionState}
              autoExpand={false}
              containerStyle={styles.accordionContainer}
              noContentBorder={true}
              copyText={copyText}
            />
          )}
        </View>
      )}
    </>
  );
};

const styles = StyleSheet.create({
  loadingContainer: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    height: 60,
    backgroundColor: theme.colors.lightPurple,
    borderRadius: theme.borderRadius.large,
  },
  container: {
    flexDirection: 'column',
    gap: 10,
  },
  title: {
    ...baseStyles.title,
    paddingHorizontal: theme.spacing.medium,
    paddingVertical: theme.spacing.xsmall,
    fontSize: theme.fontSizes.small,
  },
  accordionContainer: {
    backgroundColor: theme.colors.lightPurple,
    borderWidth: 0,
  },
  viewButton: {
    borderRadius: theme.borderRadius.xlarge,
  },
  viewButtonText: {
    fontSize: theme.fontSizes.small,
    fontWeight: theme.fontWeights.bold,
  },
  viewButtonExpanded: {
    backgroundColor: theme.colors.transparent,
    borderWidth: 1,
  },
  contentContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing.medium,
  },
  contentText: {
    fontSize: theme.fontSizes.small,
  },
  studentsContainer: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    gap: theme.spacing.medium,
    backgroundColor: theme.colors.white,
    borderRadius: theme.borderRadius.large,
    paddingHorizontal: theme.spacing.large,
    paddingVertical: theme.spacing.medium,
  },
});
